import type { Control } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ControlledNumberFormInput } from '#components/form/FormInput.tsx';
import type { OrderSummary } from '#domain/Order.ts';

import { CapacityWarning } from './CapacityWarning.tsx';
import type { TourFormValues } from './NewTourFormState.ts';
import { type CapacityMetric, getRelevantCapacityMetrics } from './tourCapacity.ts';

type TourCapacityFormProps = {
    control: Control<TourFormValues>;
    orders: OrderSummary[];
    compact?: boolean;
    exceededDimensions?: CapacityMetric[];
};

export const TourCapacityForm = ({
    control,
    orders,
    compact = false,
    exceededDimensions = [],
}: TourCapacityFormProps) => {
    const intl = useIntl();
    const relevantMetrics = getRelevantCapacityMetrics(orders);
    const inputLayout = compact
        ? {
              formGroupClassName: 'tour-capacity-field margin-0 min-width-0',
              inputContainerClassName: 'tour-capacity-input flex-shrink-0',
              bsSize: 'sm' as const,
          }
        : {};

    if (relevantMetrics.size === 0) {
        return null;
    }

    const label = (suffix: string, defaultMessage: string) =>
        intl.formatMessage({
            id: `intl-msg:ordercom-cloud-frontend.tours.create-dialog.capacity.${suffix}`,
            defaultMessage,
        });

    const inputLabel = (suffix: string, defaultMessage: string) => {
        const fullLabel = label(suffix, defaultMessage);
        const compactId = `intl-msg:ordercom-cloud-frontend.tours.create-dialog.capacity.${suffix}-compact`;
        return {
            label: compact && intl.messages[compactId] ? intl.formatMessage({ id: compactId }) : fullLabel,
            accessibleLabel: fullLabel,
            title: fullLabel,
        };
    };

    return (
        <section
            className={
                compact
                    ? 'display-flex flex-column align-items-start gap-5 min-width-0 margin-left-auto'
                    : 'margin-top-20'
            }
            aria-labelledby='tour-capacity-title'
        >
            <h4
                id='tour-capacity-title'
                className={
                    compact
                        ? 'margin-0 text-size-12 text-normal text-color-dark display-flex align-items-center gap-5'
                        : 'margin-top-0 margin-bottom-10'
                }
            >
                {compact ? label('total', 'Total capacity') : label('title', 'Vehicle capacity')}
                {compact && exceededDimensions.length > 0 && <CapacityWarning dimensions={exceededDimensions} />}
            </h4>
            {!compact && exceededDimensions.length > 0 && <CapacityWarning dimensions={exceededDimensions} />}
            <div
                className={
                    compact
                        ? 'display-flex flex-wrap gap-10'
                        : 'display-grid grid-cols-1 grid-cols-2-sm grid-cols-4-lg gap-20'
                }
            >
                {relevantMetrics.has('weight') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxWeightT'
                        {...inputLabel('max-weight', 'Maximum total weight')}
                        addon='t'
                        min={0}
                    />
                )}
                {relevantMetrics.has('liter') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxVolumeLiter'
                        {...inputLabel('max-volume-liter', 'Maximum volume in litres')}
                        addon='l'
                        min={0}
                    />
                )}
                {relevantMetrics.has('cubicMeter') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxVolumeCubicMeter'
                        {...inputLabel('max-volume-cubic-meter', 'Maximum volume in cubic metres')}
                        addon='m³'
                        min={0}
                    />
                )}
                {relevantMetrics.has('loadingMeter') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxLoadingMeter'
                        {...inputLabel('max-loading-meter', 'Maximum loading meter')}
                        addon='Ldm'
                        min={0}
                    />
                )}
            </div>
        </section>
    );
};
